import type { ReactNode } from "react";
import { Fragment, useCallback } from "react";
import type { GridProps } from "@mui/material";
import { Typography, Stack, Checkbox, Grid } from "@mui/material";
import { PerimetreBox } from "components/Business";
import type { Commune, Perimetre, PerimetreAvecCommunesOuCads } from "models";

interface RecapitulatifDeselectionnablePerimetreAvecCommunesProps extends GridProps {
  allPerimetres: Array<PerimetreAvecCommunesOuCads>;
  perimetresSelectionnes?: Array<PerimetreAvecCommunesOuCads>;
  onPerimetreChange: (perimetres: Array<PerimetreAvecCommunesOuCads>) => void;
}

export function RecapitulatifInteractifPerimetreAvecCommunes({
  allPerimetres,
  perimetresSelectionnes,
  onPerimetreChange: onChange,
  ...gridProps
}: Readonly<RecapitulatifDeselectionnablePerimetreAvecCommunesProps>): ReactNode {
  const unselectCommune = useCallback(
    (c: Commune, p: PerimetreAvecCommunesOuCads) => {
      if (perimetresSelectionnes == null) return;
      const _per = perimetresSelectionnes
        ?.map((per) =>
          per.id === p.id ? { ...per, communes: per.communes?.filter((_c) => _c.id !== c.id) } : per
        )
        .filter((_p) => _p.communes?.length !== 0);

      onChange(_per);
    },
    [onChange, perimetresSelectionnes]
  );
  const selectCommune = useCallback(
    (c: Commune, p: PerimetreAvecCommunesOuCads) => {
      if (perimetresSelectionnes == null) return;
      const _per = perimetresSelectionnes.map((_p) =>
        _p.id === p.id ? { ..._p, communes: [..._p.communes, c] } : _p
      );

      onChange(_per);
    },
    [onChange, perimetresSelectionnes]
  );

  const unselectPerimetre = useCallback(
    (p: PerimetreAvecCommunesOuCads) => {
      onChange((perimetresSelectionnes ?? []).filter((per) => per.id !== p.id));
    },
    [onChange, perimetresSelectionnes]
  );

  if (perimetresSelectionnes == null || perimetresSelectionnes.length === 0) return null;

  return (
    <Grid container size={12} spacing={2} {...gridProps}>
      <Typography component={Grid} size={12}>
        Récapitulatif des communes sélectionnées :
        <br />
        <Typography component="span" variant="caption" color="text.secondary">
          Cliquez sur une commune pour la retirer de la sélection, ou sur un périmètre pour retirer
          toutes ses communes
        </Typography>
      </Typography>

      <Stack spacing={0}>
        {allPerimetres
          .filter((per) => perimetresSelectionnes.some((p) => p.id === per.id))
          ?.map((p) => (
            <Fragment key={p.id}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => {
                  unselectPerimetre(p);
                }}
                sx={{ cursor: "pointer" }}>
                <Checkbox checked size="small" sx={{ p: 0.5 }} />
                <PerimetreBox perimetre={p as Perimetre} />
              </Stack>
              {p.communes?.map((c) => {
                const isSelected =
                  perimetresSelectionnes
                    .find(({ id }) => id === p.id)
                    ?.communes.some(({ id }) => id === c.id) ?? false;
                return (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    key={c.id}
                    onClick={() => {
                      isSelected ? unselectCommune(c, p) : selectCommune(c, p);
                    }}
                    sx={{ cursor: "pointer", ml: 4 }}>
                    <Checkbox checked={isSelected} size="small" sx={{ p: 0.5 }} />
                    <Typography>
                      {c.libelle} <span className="grey">({c.id?.substring(0, 2)})</span>
                    </Typography>
                  </Stack>
                );
              })}
            </Fragment>
          ))}
      </Stack>
    </Grid>
  );
}
